import {Icon, Severity, Show, Spinner, ThemeColorPath, ThemeIconKey} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {AriaAttributes, ForwardedRef, forwardRef, MouseEventHandler} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

export type IconButtonPriority = 'primary' | 'secondary' | 'tertiary' | 'default' | 'info';

export interface IconButtonProps extends TestIdProps {
  ref?: ForwardedRef<HTMLButtonElement>;
  icon: ThemeIconKey;
  isDisabled?: boolean;
  isLoading?: boolean;
  severity?: Severity;
  size?: 'small' | 'default';
  iconSize?: 'default' | 'large';
  priority?: IconButtonPriority;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  autoFocus?: boolean;
  isFocused?: boolean;
  'aria-label'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: AriaAttributes['aria-expanded'];
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
}

const iconButtonVariants = cva(
  'relative flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-sm transition-[background-color] duration-200 ease-in-out disabled:opacity-40',
  {
    variants: {
      size: {
        small: 'size-6',
        default: 'size-8',
      },
      priority: {
        primary:
          'bg-[var(--color-accent)] hover:bg-[var(--color-palettes-blue-70-100)] active:bg-[var(--color-palettes-blue-80-100)]',
        secondary:
          'bg-[var(--color-palettes-neutral-30-100)] hover:bg-[var(--color-palettes-neutral-40-100)] active:bg-[var(--color-palettes-neutral-50-100)]',
        info: 'bg-[var(--color-palettes-blue-30-100)] hover:bg-[var(--color-palettes-blue-40-100)] active:bg-[var(--color-palettes-blue-50-100)]',
        tertiary:
          'border border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-white)] hover:border-[var(--color-palettes-neutral-200-100)] active:border-[var(--color-palettes-neutral-900-100)]',
        default:
          'bg-transparent hover:bg-[var(--color-palettes-neutral-30-100)] active:bg-[var(--color-palettes-neutral-40-100)]',
      },
      severity: {
        informational: '',
        warning: '',
        success: '',
        danger: '',
      },
      isUnavailable: {
        true: 'pointer-events-none',
        false: '[pointer-events:all]',
      },
      isFocused: {
        true: '[outline:var(--outline-default)_var(--color-accent)] [outline-offset:var(--outline-offset)]',
        false: '',
      },
    },
    compoundVariants: [
      {
        priority: 'primary',
        severity: 'informational',
        className:
          'bg-[var(--color-severity-informational)] hover:bg-[var(--color-severity-informational-hover)] active:bg-[var(--color-severity-informational-active)]',
      },
      {
        priority: 'primary',
        severity: 'warning',
        className:
          'bg-[var(--color-severity-warning)] hover:bg-[var(--color-severity-warning-hover)] active:bg-[var(--color-severity-warning-active)]',
      },
      {
        priority: 'primary',
        severity: 'success',
        className:
          'bg-[var(--color-severity-success)] hover:bg-[var(--color-severity-success-hover)] active:bg-[var(--color-severity-success-active)]',
      },
      {
        priority: 'primary',
        severity: 'danger',
        className:
          'bg-[var(--color-severity-danger)] hover:bg-[var(--color-severity-danger-hover)] active:bg-[var(--color-severity-danger-active)]',
      },
      {
        priority: 'tertiary',
        severity: 'informational',
        className:
          'border-[var(--color-severity-informational-active)] hover:border-[var(--color-severity-informational-active)] active:border-[var(--color-severity-informational-active)]',
      },
      {
        priority: 'tertiary',
        severity: 'warning',
        className:
          'border-[var(--color-severity-warning-active)] hover:border-[var(--color-severity-warning-active)] active:border-[var(--color-severity-warning-active)]',
      },
      {
        priority: 'tertiary',
        severity: 'success',
        className:
          'border-[var(--color-severity-success-active)] hover:border-[var(--color-severity-success-active)] active:border-[var(--color-severity-success-active)]',
      },
      {
        priority: 'tertiary',
        severity: 'danger',
        className:
          'border-[var(--color-severity-danger-active)] hover:border-[var(--color-severity-danger-active)] active:border-[var(--color-severity-danger-active)]',
      },
    ],
    defaultVariants: {
      size: 'default',
      priority: 'default',
      isUnavailable: false,
      isFocused: false,
    },
  }
);

function getIconColor(priority?: IconButtonPriority, severity?: Severity): ThemeColorPath {
  return match({priority, severity})
    .with({priority: 'primary'}, () => 'general.white' as const)
    .with({priority: 'info'}, () => 'palettes.blue.80.100' as const)
    .with({severity: Pattern.when(isNotNil)}, ({severity: currentSeverity}) =>
      match(currentSeverity)
        .with('informational', () => 'severity.informational' as const)
        .with('warning', () => 'severity.warning' as const)
        .with('success', () => 'severity.success' as const)
        .with('danger', () => 'severity.danger' as const)
        .exhaustive()
    )
    .otherwise(() => 'text.primary' as const);
}

function IconButtonComponent(props: IconButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const iconSize = props.iconSize === 'large' ? 5 : 4;

  return (
    <button
      ref={ref}
      data-testid={suffixTestId('iconButton', props)}
      onClick={props.onClick}
      disabled={props.isDisabled || props.isLoading}
      aria-label={props['aria-label']}
      aria-controls={props['aria-controls']}
      aria-expanded={props['aria-expanded']}
      aria-haspopup={props['aria-haspopup']}
      type="button"
      tabIndex={props.tabIndex}
      autoFocus={props.autoFocus}
      className={cn(
        iconButtonVariants({
          size: props.size,
          priority: props.priority,
          severity: props.severity,
          isUnavailable: props.isDisabled || props.isLoading,
          isFocused: props.isFocused,
        })
      )}
    >
      <Show when={props.isLoading}>
        <Spinner variant="overlay" size="small" data-testid={suffixTestId('loader', props)} />
      </Show>
      <Icon
        color={getIconColor(props.priority, props.severity)}
        value={props.icon}
        size={iconSize}
      />
    </button>
  );
}

export const IconButton = forwardRef(IconButtonComponent);
